import { queryOptions } from '@tanstack/react-query'
import {
  OrganizationGroupsDocument,
  OrganizationQrCodesDocument,
  QrCodeDocument,
} from '@/graphql/generated'
import { graphqlRequest } from '@/lib/api/graphql'
import { keys } from '@/lib/queries/keys'
import { nodes, toGroup, toQrCode } from '@/lib/queries/mappers'
import type { Group, QrCode } from '@/lib/types'

/**
 * Every QR code in the organization, archived included.
 *
 * One collection rather than one query per screen. The list, the archive, the
 * groups browser and the sidebar all read subsets of this, and the per-group
 * counts have to be computed across the whole set anyway — a link table cannot
 * answer "how many codes are in this group" without them. Splitting it would
 * mean four caches that can disagree with each other mid-flight.
 */
/**
 * The shared fetch. Every variant below reuses this key, so the four screens
 * that read QR codes share ONE cache entry and one request — they differ only
 * in how they filter it on the way out.
 */
function qrCodesBase(organizationId: string) {
  return {
    queryKey: keys.qrCodes(organizationId),
    queryFn: async ({ signal }: { signal: AbortSignal }) => {
      const data = await graphqlRequest(
        OrganizationQrCodesDocument,
        { organizationId },
        { signal },
      )

      if (data.constellationsCollection?.pageInfo.hasNextPage) {
        /* The grid renders everything at once, so anything past the ceiling
           would be invisible rather than paged. Say so instead of quietly
           showing a truncated workspace. */
        console.warn(
          `[qr-codes] organization ${organizationId} has more codes than the ` +
            `500-code ceiling; the list is truncated. Pagination is needed.`,
        )
      }

      return data
    },
  }
}

function selectQrCodes(
  data: Awaited<ReturnType<ReturnType<typeof qrCodesBase>['queryFn']>>,
): QrCode[] {
  return nodes(data.constellationsCollection).map((node) => toQrCode(node))
}

/*
 * The `select` functions below are declared at module scope, NOT inline in the
 * query factories. This is load-bearing, not tidiness.
 *
 * React Query memoizes a `select` result against the data AND the function's
 * identity. A factory called during render — `useSuspenseQuery(qrCodeQuery(a,
 * b))` — hands it a brand new arrow every time, so the memo never hits and every
 * render rebuilds the whole view model: new `QrCode`, new `Design`, new parsed
 * `style` object. Nothing about the data changed, only its identity.
 *
 * That churn is invisible until something depends on identity, and two things
 * do. `QrTileClient` is `React.memo`'d specifically because each square tile
 * spins up its own `qr-code-styling` instance, and a fresh `style` prop defeats
 * it on every grid render. Worse, the design panel re-seeds its draft from
 * `savedStyle` when that prop changes — so an unrelated re-render (a window
 * focus refetch is enough) would discard a design the customer was part way
 * through, and now the logo they had just picked with it.
 */
const selectActiveQrCodes = (
  data: Awaited<ReturnType<ReturnType<typeof qrCodesBase>['queryFn']>>,
): QrCode[] => selectQrCodes(data).filter((qr) => !qr.isArchived)

const selectArchivedQrCodes = (
  data: Awaited<ReturnType<ReturnType<typeof qrCodesBase>['queryFn']>>,
): QrCode[] => selectQrCodes(data).filter((qr) => qr.isArchived)

export function qrCodesQuery(organizationId: string) {
  return queryOptions({ ...qrCodesBase(organizationId), select: selectQrCodes })
}

/** Just the live ones — what "All QR Codes" and the groups browser show. */
export function activeQrCodesQuery(organizationId: string) {
  return queryOptions({
    ...qrCodesBase(organizationId),
    select: selectActiveQrCodes,
  })
}

export function archivedQrCodesQuery(organizationId: string) {
  return queryOptions({
    ...qrCodesBase(organizationId),
    select: selectArchivedQrCodes,
  })
}

export function groupsQuery(organizationId: string) {
  return queryOptions({
    queryKey: keys.groups(organizationId),
    queryFn: ({ signal }) =>
      graphqlRequest(OrganizationGroupsDocument, { organizationId }, { signal }),
    select: (data): Group[] =>
      nodes(data.groupsCollection).map(toGroup),
  })
}

/**
 * A single code with its schedules.
 *
 * Separate from the list query rather than a lookup into it: the detail screen
 * needs schedules and their tasks, which nest two levels deeper and would be
 * dead weight on 500 grid cards. It is also directly linkable, so it has to
 * stand up without the list having been fetched first.
 */
function qrCodeBase(organizationId: string, constellationId: string) {
  return {
    queryKey: keys.qrCode(organizationId, constellationId),
    queryFn: ({ signal }: { signal: AbortSignal }) =>
      graphqlRequest(QrCodeDocument, { constellationId }, { signal }),
  }
}

const selectQrCode = (
  data: Awaited<ReturnType<ReturnType<typeof qrCodeBase>['queryFn']>>,
): QrCode | null => {
  const node = nodes(data.constellationsCollection)[0]
  return node ? toQrCode(node) : null
}

export function qrCodeQuery(organizationId: string, constellationId: string) {
  return queryOptions({
    ...qrCodeBase(organizationId, constellationId),
    /* Module scope, for the reason spelled out above the list selectors — this
       is the one the design panel reads, so an unstable identity here is what
       would throw away a half-finished design. */
    select: selectQrCode,
  })
}
